<template>
  <div role="listbox" :aria-label="t('search.groupPlaces')" class="px-2 pb-4">
    <p v-if="empty" data-empty class="px-3 py-6 text-center text-xs text-[hsl(var(--muted-foreground))]">{{ t('search.noResults') }}</p>
    <section v-for="g in shownGroups" :key="g.key" :data-group="g.key" class="mb-2">
      <h3 class="px-2 pt-2 pb-1 text-[11px] font-semibold text-ink2">{{ t(g.title) }}</h3>
      <SearchHitRow
        v-for="row in g.rows"
        :key="row.hit.id"
        :id="`search-opt-${row.index}`"
        :hit="row.hit"
        :distance="distanceFor(row.hit)"
        :active="row.index === activeIndex"
        @pick="$emit('pick', row.hit)"
      />
      <button v-if="g.hidden > 0" type="button" :data-more="g.key" class="px-2 py-1 text-[12px] font-semibold text-forest" @click="expanded[g.key] = true">
        {{ t('search.more', { n: g.hidden }) }}
      </button>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import SearchHitRow from "./SearchHitRow.vue";
import type { DistanceInfo } from "../../search/useSearch";
import type { SearchHit, SearchResult } from "../../search/types";

const props = defineProps<{ result: SearchResult; distanceFor: (h: SearchHit) => DistanceInfo | null }>();
const emit = defineEmits<{ (e: "pick", hit: SearchHit): void }>();
const { t } = useI18n();

type GroupKey = "places" | "routes" | "pois";
const PER_GROUP = 3;
const expanded = reactive<Record<GroupKey, boolean>>({ places: false, routes: false, pois: false });
const activeIndex = ref(-1);

// Порядок на экране: сначала точки, затем маршруты, затем места и остальное.
const GROUPS: { key: GroupKey; title: string }[] = [
  { key: "pois", title: "search.groupPoints" },
  { key: "routes", title: "search.groupRoutes" },
  { key: "places", title: "search.groupPlaces" },
];

const shownGroups = computed(() => {
  let index = 0;
  return GROUPS.flatMap((g) => {
    const all = props.result[g.key] as SearchHit[];
    if (!all.length) return [];
    const visible = expanded[g.key] ? all : all.slice(0, PER_GROUP);
    const rows = visible.map((hit) => ({ hit, index: index++ }));
    return [{ ...g, rows, hidden: all.length - visible.length }];
  });
});
const flat = computed(() => shownGroups.value.flatMap((g) => g.rows));
const empty = computed(() => flat.value.length === 0);

// Новый результат - навигация с начала.
watch(() => props.result, () => (activeIndex.value = -1));

const activeId = computed(() => (activeIndex.value >= 0 ? `search-opt-${activeIndex.value}` : undefined));

// Клавиатура: поле ввода остаётся у родителя и пробрасывает сюда keydown.
const handleKey = (e: KeyboardEvent) => {
  const n = flat.value.length;
  if (!n) return;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    activeIndex.value = (activeIndex.value + 1) % n;
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    activeIndex.value = (activeIndex.value - 1 + n) % n;
  } else if (e.key === "Enter" && activeIndex.value >= 0) {
    e.preventDefault();
    emit("pick", flat.value[activeIndex.value].hit);
  }
};

defineExpose({ handleKey, activeId });
</script>
